import React, { useCallback, useEffect, useRef } from 'react';

import { WebUI } from '../../_utils/ui';
import translate from '../../_utils/tools/translate';

import fireTrackingEvent from './events/fire-tracking-event';
import ProgressButton from '../../_utils/progress-button';
import sendToGoogleAnalytics from '../../_libs/analytics/builder-send-to-google-analytics';

const buttonText = `Add to ${translate('Cart')}`;

// Backstop only: nothing else clears the spinner when a save neither succeeds
// nor reports failure, such as one still in flight or a cancelled navigation.
const SPINNER_TIMEOUT_MS = 20000;

const AddToCartControls = ({ basketUrl, enabled, onAddToCart }) => {
  // The button stays clickable while it spins, and every accepted click fires
  // its own tracking events, so a second one double-counts the conversion.
  const inProgress = useRef(false);
  const backstop = useRef(null);

  const release = useCallback((button) => {
    clearTimeout(backstop.current);
    backstop.current = null;
    inProgress.current = false;
    button.reset();
  }, []);

  useEffect(() => () => clearTimeout(backstop.current), []);

  const handleAddToCart = useCallback(
    (e, button) => {
      e.preventDefault();

      if (enabled && !inProgress.current) {
        inProgress.current = true;
        // Owned here rather than handed to ProgressButton's timeoutMs, so that
        // releasing early also cancels it. An uncancellable timer from a
        // released click would go on to clear a later click's spinner.
        backstop.current = setTimeout(
          () => release(button),
          SPINNER_TIMEOUT_MS,
        );

        onAddToCart(
          () => {
            sendToGoogleAnalytics('Add-to-cart', 'checkout');
            fireTrackingEvent(WebUI.PHOTO_EVENT_ADD_TO_CART);
            location.href = basketUrl;
          },
          () => release(button),
        );
      }
    },
    [enabled, onAddToCart, basketUrl, release],
  );

  return (
    <ProgressButton
      aria-label={buttonText}
      className="btn btn-primary btn-add-to-cart"
      data-test="add-to-cart"
      disabled={!enabled}
      href={basketUrl}
      onClick={handleAddToCart}>
      {buttonText}
    </ProgressButton>
  );
};

export default AddToCartControls;
